// Khu vực và ngôn ngữ nằm ở localStorage — server không đọc được. Ghi thêm một
// bản sang cookie để server biết mà dựng sẵn khung trang chủ đúng biến thể
// (xem server/controllers/cacheAPI/homePage.js).
//
// An toàn kể cả khi CloudFront không chuyển tiếp cookie: server không thấy cookie
// thì dùng mặc định, và client luôn đối chiếu region/lang/seed trong khối dựng sẵn
// trước khi dùng — lệch thì bỏ qua, không bao giờ hiển thị nhầm.
//
// Không đặt dữ liệu nhạy cảm ở đây: cookie này đi kèm mọi request và có thể lọt
// vào cache dùng chung.
const ONE_YEAR_SECONDS = 365 * 24 * 60 * 60;

type HintName = 'region' | 'lang' | 'auth' | 'offers';

function readPreloadHintCookie(name: HintName): string | null {
  try {
    const match = new RegExp(`(?:^|;\\s*)${name}=([^;]*)`).exec(document.cookie);
    return match ? match[1] : null;
  } catch {
    return null;
  }
}

export function setPreloadHintCookie(name: HintName, value: string): void {
  if (!value || !/^[a-zA-Z-]{2,10}$/.test(value)) {
    return;
  }
  try {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${name}=${value}; path=/; max-age=${ONE_YEAR_SECONDS}; SameSite=Lax${secure}`;
  } catch {}
}

function clearPreloadHintCookie(name: HintName): void {
  try {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax${secure}`;
  } catch {}
}

/**
 * MỌI chỗ đổi ngôn ngữ phải đi qua đây — đừng gọi `localStorage.setItem('lang')`
 * trực tiếp.
 *
 * Vì sao: inline script ở đầu <head> (`internals/webpack/lcpEnBannerMeta.js`)
 * quyết định có preload chunk `i18n-en` + banner EN hay không, và nó CHỈ đọc
 * được cookie. Đọc localStorage ở đó sẽ buộc engine nạp cả store đồng bộ trước
 * FCP — chi phí đánh vào 100% khách, kể cả VI. Nên localStorage vẫn là nguồn
 * thật của app, cookie là bản sao cho critical path; hai cái phải ghi cùng lúc,
 * nếu lệch thì khách EN mất preload và quay lại chuỗi nối tiếp cũ (+1425ms tới
 * cuộc gọi API đầu tiên, đo QA 2026-08-03).
 */
export function setLang(value: string): void {
  try {
    window.localStorage.setItem('lang', value);
  } catch {}
  setPreloadHintCookie('lang', value);
}

// ─────────────────────────── gợi ý đã-đăng-nhập ───────────────────────────
//
// KHÔNG phải token, KHÔNG dùng để xác thực. Chỉ là một bit cho server biết dựng
// biến thể nào của lcp-shell: người đã đăng nhập không có banner khách và không
// có dải GuaranteeBar (`MainLayout.tsx`: `{!loginned && <HomePageBottomHeader/>}`).
//
// Vì sao phải là cookie chứ không đọc localStorage: shell là HTML tĩnh do server
// dựng, server không thấy được localStorage. Còn nếu để script phía client tự
// sửa shell thì nó phải chạy trước lần vẽ đầu — đo QA 2026-08-06 cho biên chỉ
// 24ms (script 1612ms, FCP 1636ms), vì script đồng bộ còn phải đợi stylesheet.
// Sai một nhịp là người đã đăng nhập thấy banner khách nháy lên.
//
// Cookie lệch với localStorage thì hậu quả nhẹ và tự lành: shell vẽ sai biến thể
// trong ~1 giây rồi React mount đè lên, và `syncAuthHintCookie()` ở app.tsx chỉnh
// lại ngay lần boot kế tiếp.
const AUTH_HINT_VALUE = 'yes';

/** MỌI chỗ lưu token phải đi qua đây — đừng gọi `localStorage.setItem('token')`. */
export function setToken(token?: string | null): void {
  if (!token) {
    return;
  }
  try {
    window.localStorage.setItem('token', token);
  } catch {}
  setPreloadHintCookie('auth', AUTH_HINT_VALUE);
}

/** MỌI chỗ xoá token phải đi qua đây — đừng gọi `localStorage.removeItem('token')`. */
export function clearToken(): void {
  try {
    window.localStorage.removeItem('token');
  } catch {}
  clearPreloadHintCookie('auth');
}

/**
 * Chỉnh cookie khớp lại với localStorage lúc boot. Cần cho hai trường hợp:
 * phiên đăng nhập có TRƯỚC khi tính năng này ra đời (chưa có cookie), và cookie
 * còn sót sau khi token bị xoá ở đường khác.
 */
// ───────────────── gợi ý có mục "Ưu đãi đặc biệt" trên trang chủ ─────────────────
//
// MỘT BIT, không phải nội dung voucher. Voucher đổi theo thời gian (startDate /
// endDate, admin bật tắt bất cứ lúc nào) nên KHÔNG cache được — nội dung luôn
// fetch tươi từ client, cookie này không mang data.
//
// Nó chỉ để server dựng sẵn ĐÚNG HÌNH HỌC khối hero: có voucher thì sidebar
// 280x550 + banner 1016x344 + strip 190px, không thì 230x360 + banner 1066x360.
// Thiếu bit này thì shell vẽ 230px, React mount cũng 230px, tới lúc data về mới
// nhảy sang 280px — banner (phần tử LCP) co lại SAU khi đã vẽ và Flash Deals tụt
// 189px.
//
// Bit sai thì hậu quả nhẹ và tự lành y như cookie `auth`: shell vẽ sai khung
// khoảng một giây rồi React đè lên, và lần fetch nào cũng ghi lại bit mới. Không
// cần hàm sync lúc boot vì mỗi lần vào trang chủ đều fetch lại voucher.
const OFFERS_HINT_VALUE = 'yes';

/** Gọi sau MỖI lần fetch voucher xong, kể cả khi rỗng (để xoá bit cũ). */
export function setOffersHint(hasOffers: boolean): void {
  if (hasOffers) {
    setPreloadHintCookie('offers', OFFERS_HINT_VALUE);
  } else {
    clearPreloadHintCookie('offers');
  }
}

export function readOffersHint(): boolean {
  return readPreloadHintCookie('offers') === OFFERS_HINT_VALUE;
}

export function syncAuthHintCookie(): void {
  let hasToken = false;
  try {
    hasToken = !!window.localStorage.getItem('token');
  } catch {}
  if (hasToken) {
    setPreloadHintCookie('auth', AUTH_HINT_VALUE);
  } else {
    clearPreloadHintCookie('auth');
  }
}
